import { of as observableOf, Observable } from 'rxjs'
import { Component, ViewChild, Input, DoCheck } from '@angular/core'
import {
  IAddress,
  PayerAddressODataService,
  ProviderAddressODataService,
  IState,
  PayerStateODataService,
  ProviderStateODataService,
  ProviderODataService,
  IProvider
} from '@exchange/domain/.'
import { TooltipService } from '@exchange/ui/shared/controls'
import { UtilService } from '@exchange/services/.'
import { AppSelectComponent } from 'common-components'
import { ToastrService } from 'ngx-toastr'
import { Option } from 'common-components'
import { mergeMap } from 'rxjs/operators'
import { ODataService } from 'common-services'

@Component({
  selector: 'app-address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss'],
  standalone: false
})
export class AddressComponent implements DoCheck {
  differ: any = null
  busy: any = null
  sameAs = false
  states: Option[] = []
  facilities: Option[] = []

  private _model: IAddress = { CountryId: 1 } as IAddress
  private _usePayer = true
  private _provider: IProvider = null
  private addressService: ODataService<IAddress>
  private stateService: ODataService<IState>
  private oldOverrideId = null

  @Input() public title = 'Address'
  @Input() linkedComponent: AddressComponent = null
  @Input() isRequired = true

  @ViewChild('stateList', { static: true }) stateList: AppSelectComponent
  @ViewChild('facilitiesList', { static: true }) facilitiesList: AppSelectComponent

  get model(): IAddress {
    return this._model
  }

  @Input() set model(value: IAddress) {
    if (!value) {
      value = { CountryId: 1 } as IAddress
    }
    this._model = value
    if (this.states.length && this.model && this.model.StateId) {
      this.stateList.active = [this.states.find(x => x.id === this.model.StateId)]
    }

    this.initializeSameAs()
  }

  private initializeSameAs() {
    // The linked model and the current model are the "same" if they
    //   1) have at least a portion of an address value, and
    //   2) all the values match
    this.sameAs =
      this.linkedComponent &&
      (this.model.Address1 ||
        this.model.Address2 ||
        this.model.ZipCode ||
        this.model.CityName ||
        this.model.StateId) &&
      this.model.Address1 === this.linkedComponent.model.Address1 &&
      this.model.Address2 === this.linkedComponent.model.Address2 &&
      this.model.ZipCode === this.linkedComponent.model.ZipCode &&
      this.model.CityName === this.linkedComponent.model.CityName &&
      this.model.StateId === this.linkedComponent.model.StateId
  }

  @Input() set usePayer(value: boolean) {
    this._usePayer = value
    if (value) {
      this.addressService = this.payerAddressService
      if (this.stateService !== this.payerStateService) {
        this.stateService = this.payerStateService
        this.loadStates()
      }
    } else {
      this.addressService = this.providerAddressService
      if (this.stateService !== this.providerStateService) {
        this.stateService = this.providerStateService
        this.loadStates()
      }
    }
  }
  get usePayer(): boolean {
    return this._usePayer
  }

  get provider(): IProvider {
    return this._provider
  }

  @Input() set provider(value: IProvider | null) {
    if (value) {
      this._provider = value
      this.loadFacilities()
    }
  }

  constructor(
    private payerAddressService: PayerAddressODataService,
    private providerAddressService: ProviderAddressODataService,
    private payerStateService: PayerStateODataService,
    private providerStateService: ProviderStateODataService,
    private providerService: ProviderODataService,
    private tooltipService: TooltipService,
    public util: UtilService,
    private toastr: ToastrService
  ) {}

  loadStates(): void {
    this.stateService
      .query()
      .orderBy('Abbreviation')
      .exec()
      .subscribe(response => {
        this.states = response.data.map(x => new Option(x.Id, `${x.Abbreviation} - (${x.Name})`, x))

        if (this.model && this.model.StateId) {
          this.stateList.active = [this.states.find(x => x.id === this.model.StateId)]
        }
      })
  }

  loadFacilities(): void {
    this.providerService
      .query()
      .orderBy('Name')
      .filter(
        `MarketId eq ${this.provider.MarketId} and Id ne ${this.provider.Id} and IsAnchor eq true`
      )
      .expand('PhysicalAddress')
      .exec()
      .subscribe(response => {
        this.facilities = response.data.map(
          facility => new Option(facility.Id, facility.Name, facility)
        )
      })
  }

  selectedFacility = (e: any = null) => {
    if (e) {
      // Don't save over the original ID if a user selects another facility again or the same one again
      if (!this.oldOverrideId) {
        this.oldOverrideId = this.model.Id
      }
      this.model.Id = e.value.PhysicalAddress.Id
      this.model.Address1 = e.value.PhysicalAddress.Address1
      this.model.Address2 = e.value.PhysicalAddress.Address2
      this.model.ZipCode = e.value.PhysicalAddress.ZipCode
      this.model.CityName = e.value.PhysicalAddress.CityName
      this.model.StateId = e.value.PhysicalAddress.StateId
      if (this.model.StateId) {
        this.stateList.active = [this.states.find(x => x.id === this.model.StateId)]
      }
    } else if (this.oldOverrideId) {
      // Reset back to original if it exists
      this.addressService
        .get(this.oldOverrideId)
        .exec()
        .subscribe(response => (this.model = response))
      this.oldOverrideId = null
    } else {
      this.model = { CountryId: 1 } as IAddress
      this.stateList.active = []
    }
  }

  selectedState = (e: any = null) => {
    this.model.StateId = (e && e.id) || null
  }

  formClick(e: any): void {
    this.tooltipService.hideCurrent(e)
  }

  validate(isAnchorProvider?: boolean): string[] {
    const errors: string[] = []

    if (this.isRequired || this.hasData()) {
      if (!this.model.Address1) {
        errors.push(`${this.title} - Address 1 is required.`)
      }

      if (!this.model.CityName) {
        errors.push(`${this.title} - City is required.`)
      }

      if (!this.model.StateId) {
        errors.push(`${this.title} - State is required.`)
      }

      if (!this.model.ZipCode) {
        errors.push(`${this.title} - Zip Code is required.`)
      }

      if (isAnchorProvider && this.model.ZipCode.length !== 9) {
        errors.push(`${this.title} - Zip Code of anchor provider must be 9 digits`)
      }
    }

    return errors
  }

  hasData(): boolean {
    return !!(
      this.model.Address1 ||
      this.model.Address2 ||
      this.model.StateId ||
      this.model.CityName ||
      this.model.ZipCode
    )
  }

  save(): Observable<IAddress> {
    const errors = this.validate()
    this.toastr.clear()
    if (errors.length > 0) {
      this.toastr.error(errors.join('<br />'))
      return null
    }

    if (!this.isRequired && !this.hasData()) {
      return observableOf(null)
    }

    return this.model.Id
      ? this.addressService.put(this.model, this.model.Id).exec()
      : this.addressService.post(this.model).exec()
  }

  saveProviderOverrideAddress(): Observable<IAddress> {
    return this.providerService
      .query()
      .filter(`PhysicalAddressId eq ${this.model.Id} or MailingAddressId eq ${this.model.Id}`)
      .expand('PhysicalAddress')
      .exec()
      .pipe(
        mergeMap(response => {
          if (response.data[0]) {
            const hasDifferences = this.checkForDifferences(response.data[0].PhysicalAddress)

            if (hasDifferences) {
              // Attempt to delete custom override address
              if (this.oldOverrideId) {
                this.deleteOverridenAddress()
              }

              this.model.Id = 0
              return this.save()
            }

            // Attempt to delete custom override address
            if (this.oldOverrideId) {
              this.deleteOverridenAddress()
            }

            return this.addressService.get(this.model.Id).exec()
          }

          return this.save()
        })
      )
  }

  deleteAddress(): void {
    if (!this.isRequired && !this.hasData() && this.model.Id) {
      this.addressService
        .delete(this.model.Id)
        .exec()
        .subscribe()
      this.model.Id = 0
    }
  }

  onSubmit(): void {
    return
  }

  checkForDifferences(dbEntry: IAddress) {
    return (
      this.model.Address1 !== dbEntry.Address1 ||
      this.model.Address2 !== dbEntry.Address2 ||
      this.model.CityName !== dbEntry.CityName ||
      this.model.StateId !== dbEntry.StateId ||
      this.model.ZipCode !== dbEntry.ZipCode
    )
  }

  deleteOverridenAddress() {
    this.providerService
      .query()
      .filter(
        `PhysicalAddressId eq ${this.oldOverrideId} or MailingAddressId eq ${this.oldOverrideId}`
      )
      .exec()
      .subscribe(response => {
        // If old address ID is not associated with a facility, abort the children
        if (!response.data[0]) {
          this.addressService
            .delete(this.oldOverrideId)
            .exec()
            .subscribe()
          this.oldOverrideId = null
        }
      })
  }

  ngDoCheck() {
    if (this.linkedComponent && this.sameAs && this.states.length > 0) {
      this.model.Address1 = this.linkedComponent.model.Address1
      this.model.Address2 = this.linkedComponent.model.Address2
      this.model.ZipCode = this.linkedComponent.model.ZipCode
      this.model.CityName = this.linkedComponent.model.CityName
      this.model.StateId = this.linkedComponent.model.StateId
      this.model.CountryId = this.linkedComponent.model.CountryId

      if (this.model && this.model.StateId) {
        this.stateList.active = [this.states.find(x => x.id === this.model.StateId)]
      }
    }
  }
}
